// PrimeReact
import { Splitter, SplitterPanel } from "primereact/splitter";

// Toolkit
import {
  PanelWithDrawers,
  TopPanel,
  Drawer,
  DrawerContent,
} from "sj-ui-toolkit/src/atlas/components/PanelWithDrawers";
import { themeClasses as T, C } from "sj-ui-toolkit/src/atlas/utils/themeClasses";
import { useMediaQuery, MEDIA_QUERY_BELOW_XL } from "sj-ui-toolkit/src/hooks";
import { SandboxLessonDetails } from "sj-ui-toolkit/src/client";

// Contexts
import { SandboxProvider } from "./contexts/SandboxProvider";
import { TabsProvider } from "./contexts/Tabs";

// Sub-components
import LeftPanel from "./LeftPanel/LeftPanel";
import SandboxControls from "./RightPanel/SandboxControls";
import OutputPanel from "./OutputPanel/OutputPanel";

// Styles
import S from "../_Lesson.module.scss";

interface SandboxLessonProps {
  lessonDetails: SandboxLessonDetails;
  tournamentUuid?: string;
  onLessonFinish: () => void;
  onSandboxReady?: () => void;
}

const SandboxLesson = ({
  lessonDetails,
  tournamentUuid,
  onLessonFinish,
  onSandboxReady,
}: SandboxLessonProps) => {
  const isStacked = useMediaQuery(MEDIA_QUERY_BELOW_XL);

  return (
    <SandboxProvider
      lessonDetails={lessonDetails}
      onLessonFinish={onLessonFinish}
      tournamentUuid={tournamentUuid}
      onSandboxReady={onSandboxReady}
    >
      {isStacked ? (
        // Below xl, Instructions and Output fold into SandboxControls's own TabView
        // instead of a separate Splitter panel and drawer.
        <div className="h-full min-h-0 flex flex-col">
          <TabsProvider>
            <SandboxControls
              isCtf={lessonDetails.isCtf}
              tabs={lessonDetails.sandbox.tabs}
              engines={lessonDetails.engines}
              lessonDetails={lessonDetails}
            />
          </TabsProvider>
        </div>
      ) : (
        <Splitter
          className={C(T.splitterGap, "h-full")}
          aria-label="Resize lesson panels"
          data-testid="sandbox-lesson"
        >
          <SplitterPanel size={30} minSize={20} className={C("flex flex-col h-full", S.leftPanel)}>
            <LeftPanel lessonDetails={lessonDetails} />
          </SplitterPanel>
          <SplitterPanel size={70} minSize={40} className="max-h-full">
            <PanelWithDrawers resizable={true} className="h-full">
              <TopPanel className="bg-elevation-1 rounded-l-sm p-3">
                <TabsProvider>
                  <SandboxControls
                    isCtf={lessonDetails.isCtf}
                    tabs={lessonDetails.sandbox.tabs}
                    engines={lessonDetails.engines}
                    lessonDetails={lessonDetails}
                  />
                </TabsProvider>
              </TopPanel>
              <Drawer
                className="bg-elevation-1 rounded-tl-sm pl-3"
                overflow="auto"
                title="Sandbox output"
              >
                <DrawerContent className="h-full">
                  <OutputPanel />
                </DrawerContent>
              </Drawer>
            </PanelWithDrawers>
          </SplitterPanel>
        </Splitter>
      )}
    </SandboxProvider>
  );
};

export default SandboxLesson;
